// ManualTripCreator — thin orchestrator container (behavior-preserving refactor,
// issue #240). Composes the extracted draft + create hooks with the presentation
// components under `./manualTripCreator/`. Same path, same default export, same
// props `{ onTripCreated, user }`. Behavior is byte-identical to the pre-refactor
// component — the characterization snapshots and the parity/contract baselines in
// `manualTripCreator/__tests__/` are the regression oracle.
//
// Validates: Requirements 1.1, 1.2, 1.3, 1.4, 3.1

import React from 'react';
import QuickSetupPrompt from './QuickSetupPrompt';
import { useTripDraft } from './manualTripCreator/hooks/useTripDraft';
import { useCreateTrip } from './manualTripCreator/hooks/useCreateTrip';
import { FormShell } from './manualTripCreator/presentation/FormShell';
import { TripFormFields } from './manualTripCreator/presentation/TripFormFields';
import { FlightSection } from './manualTripCreator/presentation/FlightSection';
import { FormFooter } from './manualTripCreator/presentation/FormFooter';

function ManualTripCreator({ onTripCreated, user }) {
  const draft = useTripDraft();
  const create = useCreateTrip({
    formData: draft.formData,
    flights: draft.flights,
    user,
    setError: draft.setError,
    setFormData: draft.setFormData,
    setFlights: draft.setFlights,
    setShowFlightLookup: draft.setShowFlightLookup,
    onTripCreated
  });

  return (
    <FormShell
      onSubmit={create.handleSubmit}
      afterForm={
        create.showQuickSetup && create.createdTrip && (
          <QuickSetupPrompt
            tripId={create.createdTrip.tripId}
            userId={create.createdTrip.userId}
            flights={create.createdTrip.flights || []}
            onComplete={create.finishQuickSetup}
            onDismiss={create.finishQuickSetup}
          />
        )
      }
    >
      <TripFormFields
        formData={draft.formData}
        isMobile={draft.isMobile}
        isCreating={create.isCreating}
        onInputChange={draft.handleInputChange}
      />

      <FlightSection
        showFlightLookup={draft.showFlightLookup}
        showFlightSearch={draft.showFlightSearch}
        isCreating={create.isCreating}
        flights={draft.flights}
        formData={draft.formData}
        onSearchClick={draft.openSearch}
        onLookupClick={draft.openLookup}
        onFlightFound={draft.onFlightFound}
        onCancelSearch={draft.cancelSearch}
        onCancelLookup={draft.cancelLookup}
        onRemoveFlight={draft.handleRemoveFlight}
        getDefaultDepartureAirport={draft.getDefaultDepartureAirport}
        getDefaultArrivalAirport={draft.getDefaultArrivalAirport}
      />

      <FormFooter
        error={draft.error}
        isCreating={create.isCreating}
        onReset={draft.handleReset}
      />
    </FormShell>
  );
}

export default ManualTripCreator;
